import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  detectUnbilled, detectUnderpriced, suggestRuleFromAnomaly, dismissAnomaly,
  type UnbilledRow, type UnderpricedRow,
} from "@/lib/billing-anomalies.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Sparkles, ArrowRight, X as XIcon, TrendingDown, ArrowLeft, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { runServerFn } from "@/lib/server-fn";

export const Route = createFileRoute("/_authenticated/app/billing/anomalies")({
  component: AnomaliesPage,
});

const fmtUsd = (cents: number) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format((cents ?? 0) / 100);

type Suggestion = Awaited<ReturnType<typeof suggestRuleFromAnomaly>> | null;

function AnomaliesPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const unbilledFn = useServerFn(detectUnbilled);
  const underpricedFn = useServerFn(detectUnderpriced);
  const suggestFn = useServerFn(suggestRuleFromAnomaly);
  const dismissFn = useServerFn(dismissAnomaly);

  const [days, setDays] = useState(30);
  const [months, setMonths] = useState(3);

  const { data: unbilled, isLoading: lu } = useQuery({
    queryKey: ["unbilled", tenantId, days],
    enabled: !!tenantId,
    queryFn: () => unbilledFn({ data: { tenantId: tenantId!, days } }),
  });
  const { data: underpriced, isLoading: lp } = useQuery({
    queryKey: ["underpriced", tenantId, months],
    enabled: !!tenantId,
    queryFn: () => underpricedFn({ data: { tenantId: tenantId!, months } }),
  });

  const [open, setOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<UnbilledRow | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState<Suggestion>(null);

  const openSuggest = async (row: UnbilledRow) => {
    if (!tenantId) return;
    setActiveRow(row); setSuggestion(null); setSuggesting(true); setOpen(true);
    try {
      const res = await runServerFn(
        () => suggestFn({ data: {
          tenantId, customerId: row.customerId, category: row.category,
          activityCount: row.activityCount, currentRuleType: row.suggestedRuleType,
        } }),
        "Suggest billing rule",
      );
      setSuggestion(res);
    } finally { setSuggesting(false); }
  };

  const dismiss = async (row: UnbilledRow) => {
    if (!tenantId) return;
    try {
      await runServerFn(
        () => dismissFn({ data: { tenantId, customerId: row.customerId, category: row.category } }),
        "Dismiss",
      );
      toast.success("Anomaly dismissed");
      qc.invalidateQueries({ queryKey: ["unbilled", tenantId] });
    } catch { /* toast handled */ }
  };

  const totalUnbilled = unbilled?.totalImpactCents ?? 0;
  const totalLeak = (underpriced?.rows ?? []).reduce((s, r) => s + r.monthlyLeakCents, 0);

  return (
    <>
      <PageHeader
        title="Billing Anomalies"
        description="What should have been invoiced — and what's bleeding margin."
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/billing"><ArrowLeft className="h-4 w-4 mr-2" />Back to billing</Link>
          </Button>
        }
      />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border rounded-lg bg-card p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> Potentially unbilled</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{fmtUsd(totalUnbilled)}</div>
            <div className="text-xs text-muted-foreground mt-1">Last {days} days</div>
          </div>
          <div className="border rounded-lg bg-card p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Monthly margin leak</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums text-destructive">{fmtUsd(totalLeak)}</div>
            <div className="text-xs text-muted-foreground mt-1">Underpriced customers</div>
          </div>
          <div className="border rounded-lg bg-card p-4">
            <div className="text-xs text-muted-foreground">Open anomalies</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{(unbilled?.rows.length ?? 0) + (underpriced?.rows.length ?? 0)}</div>
          </div>
        </div>

        <Tabs defaultValue="unbilled">
          <TabsList>
            <TabsTrigger value="unbilled">Unbilled events ({unbilled?.rows.length ?? 0})</TabsTrigger>
            <TabsTrigger value="underpriced">Underpriced customers ({underpriced?.rows.length ?? 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="unbilled" className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Window</span>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border rounded-lg bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Activity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Est. impact</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {lu && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Scanning…</TableCell></TableRow>}
                  {!lu && (unbilled?.rows ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No unbilled events found in this window — everything matches a billing rule.</TableCell></TableRow>
                  )}
                  {(unbilled?.rows ?? []).map((r) => (
                    <TableRow key={`${r.customerId}-${r.category}`}>
                      <TableCell className="font-medium">{r.customerName}</TableCell>
                      <TableCell>{r.categoryLabel}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.activityCount.toLocaleString()}</TableCell>
                      <TableCell>
                        {r.reason === "no_rule"
                          ? <Badge variant="destructive">No rule</Badge>
                          : <Badge className="bg-amber-500 text-white hover:bg-amber-500">Rule not firing</Badge>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmtUsd(r.estimatedImpactCents)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => openSuggest(r)}>
                            <Sparkles className="h-3.5 w-3.5 mr-1" />Suggest rule
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => dismiss(r)} title="Dismiss for this window">
                            <XIcon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="underpriced" className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Lookback</span>
              <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 month</SelectItem>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground ml-auto">Threshold: margin &lt; 10%</span>
            </div>
            <div className="border rounded-lg bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                  <TableHead className="text-right">Monthly leak</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {lp && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>}
                  {!lp && (underpriced?.rows ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No customers below threshold — pricing looks healthy.</TableCell></TableRow>
                  )}
                  {(underpriced?.rows ?? []).map((r: UnderpricedRow) => (
                    <TableRow key={r.customerId}>
                      <TableCell className="font-medium">{r.customerName}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUsd(r.revenueCents)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtUsd(r.costCents)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${r.marginCents < 0 ? "text-destructive" : ""}`}>{fmtUsd(r.marginCents)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.marginPct === null ? "—" : `${r.marginPct.toFixed(1)}%`}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-destructive">{fmtUsd(r.monthlyLeakCents)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" asChild>
                          <Link to="/app/billing/customers/$customerId" params={{ customerId: r.customerId }}>
                            Rate card<ArrowRight className="h-3.5 w-3.5 ml-1" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />Suggested billing rule
            </DialogTitle>
          </DialogHeader>
          {suggesting && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Analyzing peer rules and recent activity…
            </div>
          )}
          {!suggesting && suggestion && activeRow && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-muted-foreground">Customer</div><div className="font-medium">{activeRow.customerName}</div></div>
                <div><div className="text-xs text-muted-foreground">Category</div><div className="font-medium">{activeRow.categoryLabel}</div></div>
                <div><div className="text-xs text-muted-foreground">Rule type</div><div className="font-mono text-xs">{suggestion.ruleType}</div></div>
                <div><div className="text-xs text-muted-foreground">Unit price</div><div className="font-medium tabular-nums">{fmtUsd(suggestion.unitPriceCents)}</div></div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Description</div>
                <div>{suggestion.description}</div>
              </div>
              <div className="border-l-2 border-primary/40 pl-3 text-muted-foreground text-xs">
                <span className="font-medium text-foreground">Rationale:</span> {suggestion.rationale}
                {suggestion.source === "heuristic" && <Badge variant="outline" className="ml-2">heuristic</Badge>}
                {suggestion.source === "ai" && <Badge variant="secondary" className="ml-2">AI</Badge>}
              </div>
              <div className="rounded-md bg-muted/40 p-3 text-xs">
                Apply this rule from the customer's <span className="font-medium">rate card</span> page — link below.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            {activeRow && (
              <Button asChild>
                <Link to="/app/billing/customers/$customerId" params={{ customerId: activeRow.customerId }}>
                  Open rate card<ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}